'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { EmptyState } from './EmptyState';
import { formatBucketDate } from '../utils/formatBucketDate';

interface SubscriptionStatusChartProps {
  data: Map<string, { active: number; trialing: number; pastDue: number; total: number }>;
  className?: string;
}

// Define chart colors for each status
const COLORS = {
  active: '#10b981', // Emerald - green
  trialing: '#3b82f6', // Blue
  pastDue: '#f59e0b', // Amber - orange/yellow
};

export function SubscriptionStatusChart({ data, className = '' }: SubscriptionStatusChartProps) {
  const { t } = useTranslation('analytics');
  // Helper to format date for display
  const formatDateString = (dateString: string): string => {
    if (!dateString || typeof dateString !== 'string') return '';
    return formatBucketDate(dateString);
  };

  // Convert Map to array and sort by date
  const chartData = Array.from(data.entries())
    .map(([date, values]) => ({
      date,
      [t('charts.active')]: values.active,
      [t('charts.trial')]: values.trialing,
      [t('charts.pastDue')]: values.pastDue,
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Empty state
  if (chartData.length === 0) {
    return <EmptyState message={t('charts.noStatusData')} className={className} />;
  }

  // Helper to format Y-axis values
  const formatYAxisValue = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return Math.round(value).toString();
  };

  return (
    <div className={`h-full ${className}`}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-gray-200 dark:stroke-gray-700" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            className="text-gray-600 dark:text-gray-400 text-xs"
            tickFormatter={formatDateString}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12 }}
            className="text-gray-600 dark:text-gray-400 text-xs"
            tickFormatter={formatYAxisValue}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload?.length) {
                return (
                  <div className="bg-white dark:bg-gray-800 p-3 shadow-lg rounded border border-gray-200 dark:border-gray-700">
                    <p className="text-gray-800 dark:text-gray-200 font-medium mb-2">
                      {formatDateString(String(label))}
                    </p>
                    <div className="space-y-1">
                      {payload.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between gap-4">
                          <span className="flex items-center gap-2">
                            <span
                              className="inline-block w-3 h-3 rounded"
                              style={{ backgroundColor: entry.color }}
                            />
                            <span className="text-gray-600 dark:text-gray-400 text-sm">
                              {entry.name}:
                            </span>
                          </span>
                          <span className="text-gray-800 dark:text-gray-200 font-medium text-sm">
                            {Number(entry.value).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="circle"
            formatter={(value) => (
              <span className="text-gray-600 dark:text-gray-400 text-sm">{value}</span>
            )}
          />
          <Area
            type="monotone"
            dataKey={t('charts.active')}
            stackId="1"
            stroke={COLORS.active}
            fill={COLORS.active}
            fillOpacity={0.8}
          />
          <Area
            type="monotone"
            dataKey={t('charts.trial')}
            stackId="1"
            stroke={COLORS.trialing}
            fill={COLORS.trialing}
            fillOpacity={0.8}
          />
          <Area
            type="monotone"
            dataKey={t('charts.pastDue')}
            stackId="1"
            stroke={COLORS.pastDue}
            fill={COLORS.pastDue}
            fillOpacity={0.8}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
